import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaGoogle, FaDiscord, FaGithub, FaEnvelope } from 'react-icons/fa';
import { authService, type ServerInfo, type CharacterInfo } from '../../services/SupabaseClient';
import { emitGameEvent } from '../../core/GameEvents';

const WIKI_URL = import.meta.env.VITE_WIKI_URL || 'http://localhost:3001';
const GAME_VERSION = 'v0.1.14';

// Color palette
const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DISCORD: '#5865F2',
  GOOGLE: '#4285F4',
  GITHUB: '#6e5494',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
};


// Two-font system
const FONT_DISPLAY = "'Rajdhani', sans-serif";
const FONT_DATA = "'JetBrains Mono', monospace";

// Styles
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: FONT_DATA,
  color: PALETTE.LIGHT,
  backgroundColor: PALETTE.DARK,
};

const titleStyle: React.CSSProperties = {
  fontSize: '64px',
  fontWeight: 700,
  color: PALETTE.ORANGE,
  textShadow: `0 0 10px ${PALETTE.TEAL}`,
  marginBottom: '10px',
  letterSpacing: '0.3em',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 500,
  color: PALETTE.DIM,
  letterSpacing: '1px',
  marginBottom: '5px',
};

const versionStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  color: PALETTE.DIM,
  letterSpacing: '0.5px',
  marginBottom: '40px',
};

const statusStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: PALETTE.TEAL,
  letterSpacing: '0.5px',
  marginBottom: '20px',
  minHeight: '20px',
};

const buttonStyle: React.CSSProperties = {
  padding: '10px 24px',
  fontSize: '12px',
  fontWeight: 600,
  fontFamily: FONT_DISPLAY,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginBottom: '12px',
  minWidth: '250px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  transition: 'transform 0.1s, opacity 0.2s',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: '12px',
  fontWeight: 500,
  fontFamily: FONT_DISPLAY,
  letterSpacing: '0.5px',
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  backgroundColor: PALETTE.PANEL,
  color: PALETTE.LIGHT,
  marginBottom: '12px',
  minWidth: '250px',
  outline: 'none',
};

const linkStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 500,
  color: PALETTE.DIM,
  cursor: 'pointer',
  textDecoration: 'none',
  marginTop: '10px',
  letterSpacing: '0.5px',
};

type LoginView = 'loading' | 'initial' | 'auth-options' | 'email-form'
  | 'server-select' | 'character-select' | 'character-creation' | 'ready';

export function LoginForm({ onPlay }: { onPlay: () => void }) {
  const [view, setView] = useState<LoginView>('loading');
  const [status, setStatus] = useState('Initializing...');
  const [statusColor, setStatusColor] = useState(PALETTE.TEAL);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Tutorial opt-out — default false (Albion-style: tutorial is the default path).
  // Spawn destination is geographical: if checked, server routes to Origin Constellation;
  // otherwise to the Academy with a one-way exit. Either choice is permanent.
  const [skipTutorial, setSkipTutorial] = useState(false);

  // Server selection
  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);

  // Character selection
  const [characters, setCharacters] = useState<CharacterInfo[]>([]);
  const [canCreateMore, setCanCreateMore] = useState(false);
  const [selectedCharId, setSelectedCharId] = useState<string | null>(null);
  const [suggestedUsername, setSuggestedUsername] = useState('');

  // Delete character modal
  const [deleteTarget, setDeleteTarget] = useState<CharacterInfo | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Player counts for ready view
  const [playerCount, setPlayerCount] = useState<number | null>(null);
  const [globalPlayerCount, setGlobalPlayerCount] = useState<number | null>(null);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch player counts when on character-select or ready
  useEffect(() => {
    if (view !== 'character-select' && view !== 'ready') return;

    const fetchPlayerCounts = async () => {
      try {
        const apiUrl = authService.getColyseusUrl().replace('wss://', 'https://').replace('ws://', 'http://');
        const globalResponse = await fetch(`${apiUrl}/api/player-count`);
        if (globalResponse.ok) {
          const data = await globalResponse.json();
          setGlobalPlayerCount(data.players);
        }
      } catch {
        setGlobalPlayerCount(null);
      }
    };

    fetchPlayerCounts();
    const interval = setInterval(fetchPlayerCounts, 5000);
    return () => clearInterval(interval);
  }, [view]);

  const checkAuth = async () => {
    try {
      const session = await authService.initialize();

      if (session) {
        setStatus('Connected. Select a server...');
        setStatusColor(PALETTE.TEAL);
        // Go to server selection after auth
        await loadServersAndShow();
      } else {
        setStatus('');
        setView('initial');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setStatus('Connection error. Please refresh.');
      setStatusColor(PALETTE.ORANGE);
    }
  };

  const loadServersAndShow = async () => {
    setStatus('Fetching servers...');
    let serverList = await authService.getServers();

    if (serverList.length === 0) {
      // No servers in DB — add a dev fallback
      serverList = [{
        id: 'dev',
        display_name: 'Local Dev',
        region: 'local',
        colyseus_url: import.meta.env.VITE_COLYSEUS_URL || 'ws://localhost:2567',
        status: 'online',
        max_characters: 3,
      }];
    }

    setServers(serverList);

    // Check localStorage for last selected server — auto-connect if still valid
    const lastServerId = localStorage.getItem('astra_last_server');
    const lastServer = lastServerId ? serverList.find(s => s.id === lastServerId && s.status === 'online') : null;

    if (lastServer) {
      // Auto-connect to last used server (skip server-select)
      await handleSelectServer(lastServer);
    } else {
      // Show server-select — pre-select first server
      setSelectedServerId(serverList[0].id);
      setStatus('');
      setView('server-select');
    }
  };

  const handleSelectServer = async (server: ServerInfo) => {
    setIsLoading(true);
    authService.selectServer(server);
    localStorage.setItem('astra_last_server', server.id);
    setStatus(`Connecting to ${server.display_name}...`);

    // Ensure account and fetch characters for this server
    const result = await authService.ensureAccount();
    setIsLoading(false);

    if (!result) {
      // Account couldn't be resolved on this server (DB reset, server unreachable,
      // stale token, etc.). Sign the user out so they land on a clean login screen
      // instead of a stuck "Failed to connect" loop.
      await authService.logout();
      localStorage.removeItem('astra_last_server');
      setStatus('Session ended. Please log in again.');
      setStatusColor(PALETTE.ORANGE);
      setView('initial');
      return;
    }

    setCharacters(result.characters);
    setCanCreateMore(result.canCreateMore);
    setSuggestedUsername(result.suggestedUsername);

    // Always show character-select (even with 0 characters — shows empty slots)
    if (result.characters.length > 0) {
      setSelectedCharId(result.characters[0].id);
    } else {
      setSelectedCharId(null);
    }
    setStatus('');
    setView('character-select');
  };

  // Username availability check with debounce
  useEffect(() => {
    if (view !== 'character-creation' || !username || username.length < 3) {
      setUsernameStatus('');
      setUsernameAvailable(false);
      return;
    }

    const timer = setTimeout(async () => {
      setUsernameStatus('Checking...');
      try {
        const result = await authService.checkUsername(username);
        setUsernameAvailable(result.available);
        setUsernameStatus(result.available ? 'Available!' : result.reason || 'Username taken');
      } catch {
        setUsernameStatus('Error checking');
        setUsernameAvailable(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username, view]);

  const handleOAuthLogin = (provider: 'google' | 'discord' | 'github') => {
    setView('initial');
    setStatus(`Redirecting to ${provider}...`);

    switch (provider) {
      case 'google':
        authService.loginWithGoogle();
        break;
      case 'discord':
        authService.loginWithDiscord();
        break;
      case 'github':
        authService.loginWithGithub();
        break;
    }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) {
      setStatus('Please enter email and password');
      setStatusColor(PALETTE.ORANGE);
      return;
    }

    setIsLoading(true);
    setStatus('Signing in...');

    try {
      const result = await authService.loginWithEmail(email, password);
      if (result.needsVerification) {
        setStatus('Check your email to confirm!');
        setStatusColor(PALETTE.TEAL);
      } else {
        await checkAuth();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login error. Please try again.';
      setStatus(message);
      setStatusColor(PALETTE.ORANGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCharacter = async () => {
    if (!username || !usernameAvailable) return;

    setIsLoading(true);
    setStatus('Creating pilot profile...');

    try {
      const result = await authService.createCharacter(username, { skipTutorial });
      if (result.success && result.player) {
        // Re-fetch characters from server to get full data
        const accountResult = await authService.ensureAccount();
        if (accountResult) {
          setCharacters(accountResult.characters);
          setCanCreateMore(accountResult.canCreateMore);
          // Auto-select the new character
          setSelectedCharId(result.player.id);
          authService.selectCharacter(result.player.id);
          // Use the spawn constellation returned by the server (tutorial constellation by
          // default, Origin if skipTutorial / tutorial unavailable) instead of
          // hardcoding (0, 0). selectCharacter() above already pulled the
          // refetched character's constellation, but this is the authoritative one
          // for fresh creations and is safe to set explicitly.
          authService.setPlayerConstellation(
            result.player.constellationX,
            result.player.constellationY,
            result.player.constellationName ?? `Constellation (${result.player.constellationX}, ${result.player.constellationY})`,
          );
          setStatus(`Welcome, ${username}!`);
          setView('character-select');
        }
      } else {
        setStatus(result.error || 'Failed to create profile');
        setStatusColor(PALETTE.ORANGE);
      }
    } catch (error) {
      setStatus('Failed to create profile. Please try again.');
      setStatusColor(PALETTE.ORANGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlay = () => {
    if (!selectedCharId) return;
    authService.selectCharacter(selectedCharId);
    onPlay();
  };

  const handleDeleteCharacter = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.username) return;

    setIsLoading(true);
    const result = await authService.deleteCharacter(deleteTarget.id);
    setIsLoading(false);

    if (result.success) {
      // Re-fetch characters
      const accountResult = await authService.ensureAccount();
      if (accountResult) {
        setCharacters(accountResult.characters);
        setCanCreateMore(accountResult.canCreateMore);
        if (selectedCharId === deleteTarget.id) {
          setSelectedCharId(accountResult.characters.length > 0 ? accountResult.characters[0].id : null);
        }
      }
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } else {
      setStatus(result.error || 'Failed to delete character');
      setStatusColor(PALETTE.ORANGE);
    }
  };

  const renderDeleteModal = () => {
    if (!deleteTarget) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100,
      }}
        onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
      >
        <div
          style={{
            backgroundColor: PALETTE.DARK,
            border: `1px solid ${PALETTE.ORANGE}`,
            borderRadius: '8px',
            padding: '28px 32px',
            maxWidth: '400px',
            width: '90%',
            fontFamily: FONT_DISPLAY,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: '18px', color: PALETTE.ORANGE, marginBottom: '12px', fontWeight: 600 }}>
            Delete Character
          </div>
          <div style={{ fontSize: '13px', color: PALETTE.LIGHT, marginBottom: '8px' }}>
            This will permanently delete <span style={{ color: PALETTE.TEAL, fontWeight: 600 }}>{deleteTarget.username}</span> and all their data:
          </div>
          <div style={{ fontSize: '11px', color: PALETTE.DIM, marginBottom: '16px', lineHeight: '1.6' }}>
            Ships, equipment, inventory, bank, market orders, private stations, planet structures, and bounties will be lost forever.
          </div>
          <div style={{ fontSize: '12px', color: PALETTE.LIGHT, marginBottom: '8px' }}>
            Type <span style={{ color: PALETTE.ORANGE }}>{deleteTarget.username}</span> to confirm:
          </div>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteTarget.username}
            style={{
              ...inputStyle,
              width: '100%',
              marginBottom: '16px',
              borderColor: deleteConfirmText === deleteTarget.username ? PALETTE.ORANGE : PALETTE.DIM,
              boxSizing: 'border-box',
            }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              style={{
                ...buttonStyle,
                backgroundColor: 'transparent',
                color: PALETTE.DIM,
                border: `1px solid ${PALETTE.DIM}`,
                minWidth: '100px',
                marginBottom: '0',
              }}
              onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
            >
              Cancel
            </button>
            <button
              style={{
                ...buttonStyle,
                backgroundColor: deleteConfirmText === deleteTarget.username ? '#cc3333' : PALETTE.DIM,
                color: 'white',
                minWidth: '100px',
                marginBottom: '0',
                cursor: deleteConfirmText === deleteTarget.username ? 'pointer' : 'not-allowed',
              }}
              onClick={handleDeleteCharacter}
              disabled={deleteConfirmText !== deleteTarget.username || isLoading}
            >
              {isLoading ? 'Deleting...' : 'DELETE'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Seeded PRNG for stable star positions across renders
  const starLayers = useMemo(() => {
    let seed = 42;
    const rng = () => {
      seed = (seed * 16807 + 0) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    // 3 layers: far (small, dim, slow), mid, near (bigger, brighter, faster)
    const layers = [
      { count: 400, sizeMin: 0.5, sizeMax: 1.2, opMin: 0.15, opMax: 0.35, duration: 180 },
      { count: 200, sizeMin: 1, sizeMax: 2, opMin: 0.25, opMax: 0.5, duration: 120 },
      { count: 100, sizeMin: 1.5, sizeMax: 3, opMin: 0.4, opMax: 0.7, duration: 80 },
    ];
    return layers.map((cfg, layerIdx) => ({
      ...cfg,
      stars: Array.from({ length: cfg.count }, () => ({
        x: rng() * 200,  // 200% width so it tiles when scrolling
        y: rng() * 100,
        size: cfg.sizeMin + rng() * (cfg.sizeMax - cfg.sizeMin),
        opacity: cfg.opMin + rng() * (cfg.opMax - cfg.opMin),
        twinkleDelay: rng() * 6,
        hasTwinkle: layerIdx >= 1 && rng() > 0.6,
      })),
    }));
  }, []);

  const renderStarfield = () => (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {starLayers.map((layer, layerIdx) => (
        <div
          key={layerIdx}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '200%',
            height: '100%',
            animation: `starDrift${layerIdx} ${layer.duration}s linear infinite`,
          }}
        >
          {layer.stars.map((star, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                backgroundColor: PALETTE.LIGHT,
                opacity: star.opacity,
                borderRadius: '50%',
                animation: star.hasTwinkle ? `starTwinkle ${3 + star.twinkleDelay}s ease-in-out ${star.twinkleDelay}s infinite` : undefined,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );

  const renderLoadingView = () => (
    <div
      style={{
        width: '40px',
        height: '40px',
        border: `3px solid ${PALETTE.PANEL}`,
        borderTop: `3px solid ${PALETTE.TEAL}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
  );

  const renderInitialView = () => (
    <>
      <button
        style={{ ...buttonStyle, backgroundColor: PALETTE.TEAL, color: PALETTE.DARK }}
        onClick={() => setView('auth-options')}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        LOGIN TO ENTER UNIVERSE
      </button>
      <button
        style={{ ...buttonStyle, backgroundColor: PALETTE.DISCORD, color: 'white' }}
        onClick={() => window.open('https://discord.gg/dp4gxrXdFc', '_blank')}
      >
        JOIN OUR DISCORD
      </button>
      <span
        style={linkStyle}
        onClick={() => window.open(WIKI_URL, '_blank')}
        onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
        onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
      >
        [ WIKI ]
      </span>
    </>
  );

  const renderAuthOptions = () => (
    <>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <button
          style={{
            ...buttonStyle,
            backgroundColor: PALETTE.GOOGLE,
            color: 'white',
            minWidth: 'auto',
            width: '56px',
            height: '56px',
            padding: '0',
            marginBottom: '0',
            borderRadius: '8px',
          }}
          onClick={() => handleOAuthLogin('google')}
          title="Sign in with Google"
        >
          <FaGoogle size={24} />
        </button>
        <button
          style={{
            ...buttonStyle,
            backgroundColor: PALETTE.DISCORD,
            color: 'white',
            minWidth: 'auto',
            width: '56px',
            height: '56px',
            padding: '0',
            marginBottom: '0',
            borderRadius: '8px',
          }}
          onClick={() => handleOAuthLogin('discord')}
          title="Sign in with Discord"
        >
          <FaDiscord size={24} />
        </button>
        <button
          style={{
            ...buttonStyle,
            backgroundColor: PALETTE.GITHUB,
            color: 'white',
            minWidth: 'auto',
            width: '56px',
            height: '56px',
            padding: '0',
            marginBottom: '0',
            borderRadius: '8px',
          }}
          onClick={() => handleOAuthLogin('github')}
          title="Sign in with GitHub"
        >
          <FaGithub size={24} />
        </button>
        <button
          style={{
            ...buttonStyle,
            backgroundColor: PALETTE.TEAL,
            color: PALETTE.DARK,
            minWidth: 'auto',
            width: '56px',
            height: '56px',
            padding: '0',
            marginBottom: '0',
            borderRadius: '8px',
          }}
          onClick={() => setView('email-form')}
          title="Sign in with Email"
        >
          <FaEnvelope size={24} />
        </button>
      </div>
      <span
        style={linkStyle}
        onClick={() => setView('initial')}
        onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
        onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
      >
        &larr; Back
      </span>
    </>
  );

  const renderEmailForm = () => (
    <>
      <input
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={inputStyle}
        onFocus={(e) => (e.currentTarget.style.borderColor = PALETTE.TEAL)}
        onBlur={(e) => (e.currentTarget.style.borderColor = PALETTE.DIM)}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        style={inputStyle}
        onFocus={(e) => (e.currentTarget.style.borderColor = PALETTE.TEAL)}
        onBlur={(e) => (e.currentTarget.style.borderColor = PALETTE.DIM)}
        onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
      />
      <button
        style={{ ...buttonStyle, backgroundColor: PALETTE.TEAL, color: PALETTE.DARK }}
        onClick={handleEmailLogin}
        disabled={isLoading}
      >
        {isLoading ? 'Please wait...' : 'Sign In / Sign Up'}
      </button>
      <span
        style={linkStyle}
        onClick={() => setView('auth-options')}
        onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
        onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
      >
        &larr; Back
      </span>
    </>
  );

  // ==================== SERVER SELECT ====================
  const renderServerSelect = () => {
    const selected = servers.find(s => s.id === selectedServerId) || null;
    const canConnect = selected && selected.status === 'online';

    return (
      <>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', color: PALETTE.LIGHT }}>
          Select Universe
        </div>
        <div style={{ fontSize: '12px', color: PALETTE.DIM, marginBottom: '24px' }}>
          Each server is an independent universe with its own characters
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '320px' }}>
          {servers.map((server) => (
            <button
              key={server.id}
              style={{
                ...buttonStyle,
                backgroundColor: server.id === selectedServerId ? PALETTE.PANEL : 'transparent',
                color: PALETTE.LIGHT,
                border: `1px solid ${server.id === selectedServerId ? PALETTE.TEAL : PALETTE.DIM}`,
                minWidth: '320px',
                padding: '16px 20px',
                justifyContent: 'space-between',
                opacity: server.status === 'online' ? 1 : 0.5,
                cursor: server.status === 'online' ? 'pointer' : 'not-allowed',
                marginBottom: '0',
              }}
              onClick={() => server.status === 'online' && setSelectedServerId(server.id)}
              disabled={server.status !== 'online'}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: server.id === selectedServerId ? PALETTE.TEAL : PALETTE.LIGHT,
                }}>{server.display_name}</span>
                <span style={{ fontSize: '11px', color: PALETTE.DIM }}>{server.region}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span style={{
                  fontSize: '11px',
                  color: server.status === 'online' ? PALETTE.TEAL : PALETTE.ORANGE,
                }}>
                  {server.status.toUpperCase()}
                </span>
                {server.online_players !== undefined && (
                  <span style={{ fontSize: '11px', color: PALETTE.DIM }}>
                    {server.online_players} online
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Connect button */}
        <button
          style={{
            ...buttonStyle,
            backgroundColor: canConnect ? PALETTE.ORANGE : PALETTE.DIM,
            color: PALETTE.DARK,
            fontSize: '16px',
            padding: '14px 48px',
            cursor: canConnect ? 'pointer' : 'not-allowed',
            marginTop: '20px',
          }}
          onClick={() => selected && handleSelectServer(selected)}
          disabled={!canConnect || isLoading}
        >
          {isLoading ? 'Connecting...' : 'CONNECT'}
        </button>

        <span
          style={{ ...linkStyle, marginTop: '10px' }}
          onClick={async () => {
            await authService.logout();
            localStorage.removeItem('astra_last_server');
            setView('initial');
            setStatus('');
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
          onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
        >
          Logout
        </span>
      </>
    );
  };

  // ==================== CHARACTER SELECT ====================
  const getRepLabel = (rep: number): { text: string; color: string } => {
    if (rep <= -500) return { text: 'OUTLAW', color: '#ff4444' };
    if (rep < -200) return { text: 'WANTED', color: PALETTE.ORANGE };
    if (rep < 0) return { text: 'SUSPICIOUS', color: '#cc8844' };
    if (rep < 200) return { text: 'NEUTRAL', color: PALETTE.DIM };
    if (rep < 500) return { text: 'TRUSTED', color: PALETTE.TEAL };
    return { text: 'HERO', color: '#44ff88' };
  };

  const formatShipName = (shipClassId: string | null): string => {
    if (!shipClassId) return 'Corvette T1';
    return shipClassId.replace(/_/g, ' ').replace(/\bt(\d)/g, 'T$1').replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderCharacterSelect = () => {
    const selectedChar = characters.find(c => c.id === selectedCharId) || null;
    const maxSlots = authService.maxCharacters;

    return (
      <div style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
      }}>
        {/* LEFT PANEL — Character List */}
        <div style={{
          width: '280px',
          minWidth: '280px',
          backgroundColor: 'rgba(10, 20, 30, 0.92)',
          borderRight: `1px solid ${PALETTE.DIM}`,
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          zIndex: 2,
        }}>
          {/* ASTRA Logo */}
          <div style={{
            padding: '10px 20px 10px',
          }}>
            <div style={{
              fontSize: '30px',
              color: PALETTE.ORANGE,
              textShadow: `0 0 8px ${PALETTE.TEAL}`,
              letterSpacing: '0.25em',
              fontWeight: 600,
            }}>
              ASTRA
            </div>
            <div style={{ fontSize: '10px', color: PALETTE.DIM, marginTop: '2px' }}>
              {GAME_VERSION} - Pre Alpha
            </div>
          </div>

          {/* Panel header */}
          <div style={{
            padding: '12px 20px',
            borderTop: `1px solid ${PALETTE.DIM}`,
            borderBottom: `1px solid ${PALETTE.DIM}`,
          }}>
            <div style={{ fontSize: '11px', color: PALETTE.TEAL, letterSpacing: '1.5px', fontWeight: 600 }}>
              CHARACTERS
            </div>
            <div style={{ fontSize: '10px', color: PALETTE.DIM, marginTop: '2px' }}>
              {authService.selectedServer?.display_name || 'Server'} &middot; {characters.length}/{maxSlots}
            </div>
          </div>

          {/* Character slots */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
            {characters.map((char) => {
              const isSelected = char.id === selectedCharId;
              return (
                <div key={char.id} style={{ position: 'relative', marginBottom: '2px' }}>
                  <button
                    style={{
                      width: '100%',
                      backgroundColor: isSelected ? PALETTE.PANEL : 'transparent',
                      color: PALETTE.LIGHT,
                      border: 'none',
                      borderLeft: isSelected ? `3px solid ${PALETTE.TEAL}` : '3px solid transparent',
                      padding: '10px 32px 10px 12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: FONT_DISPLAY,
                      transition: 'background-color 0.15s',
                    }}
                    onClick={() => setSelectedCharId(char.id)}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(32,165,166,0.1)'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = isSelected ? PALETTE.PANEL : 'transparent'; }}
                  >
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      color: isSelected ? PALETTE.TEAL : PALETTE.LIGHT,
                      marginBottom: '2px',
                    }}>
                      {char.username}
                    </div>
                    <div style={{ fontSize: '10px', color: PALETTE.DIM }}>
                      {formatShipName(char.shipClassId)}
                    </div>
                    <div style={{ fontSize: '10px', color: PALETTE.DIM }}>
                      {char.constellationName}
                    </div>
                  </button>
                  {/* Delete X button */}
                  <span
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      color: PALETTE.DIM,
                      cursor: 'pointer',
                      borderRadius: '50%',
                      transition: 'color 0.15s, background-color 0.15s',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(char);
                      setDeleteConfirmText('');
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#cc3333';
                      e.currentTarget.style.backgroundColor = 'rgba(204,51,51,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = PALETTE.DIM;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={`Delete ${char.username}`}
                  >
                    &times;
                  </span>
                </div>
              );
            })}

            {/* Add Character button */}
            {canCreateMore && (
              <button
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  color: PALETTE.DIM,
                  border: `1px dashed ${PALETTE.DIM}`,
                  padding: '12px',
                  cursor: 'pointer',
                  fontFamily: FONT_DISPLAY,
                  fontSize: '12px',
                  marginTop: '4px',
                  transition: 'all 0.15s',
                }}
                onClick={() => {
                  setUsername(suggestedUsername);
                  setView('character-creation');
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = PALETTE.TEAL;
                  e.currentTarget.style.color = PALETTE.TEAL;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = PALETTE.DIM;
                  e.currentTarget.style.color = PALETTE.DIM;
                }}
              >
                + Add Character
              </button>
            )}
          </div>

          {/* Bottom section */}
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${PALETTE.DIM}`, marginTop: 'auto' }}>
            {globalPlayerCount !== null && (
              <div style={{ fontSize: '10px', color: PALETTE.DIM, marginBottom: '8px' }}>
                {globalPlayerCount} {globalPlayerCount === 1 ? 'pilot' : 'pilots'} online
              </div>
            )}
            <div style={{ display: 'flex', gap: '16px' }}>
              <span
                style={{ ...linkStyle, fontSize: '10px', letterSpacing: '0.5px', marginTop: '0' }}
                onClick={() => setView('server-select')}
                onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
                onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
              >
                Change Server
              </span>
              <span
                style={{ ...linkStyle, fontSize: '10px', letterSpacing: '0.5px', marginTop: '0' }}
                onClick={async () => {
                  await authService.logout();
                  localStorage.removeItem('astra_last_server');
                  setView('initial');
                  setStatus('');
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
                onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
              >
                Logout
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL — Character Preview (fills remaining space) */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {selectedChar ? (
            <>
              {/* Character name */}
              <div style={{
                fontSize: '26px',
                fontWeight: 700,
                color: PALETTE.TEAL,
                marginBottom: '6px',
                letterSpacing: '0.08em',
              }}>
                {selectedChar.username}
              </div>

              {/* Ship class */}
              <div style={{ fontSize: '12px', fontWeight: 500, color: PALETTE.LIGHT, marginBottom: '32px', opacity: 0.7, letterSpacing: '0.5px' }}>
                {formatShipName(selectedChar.shipClassId)}
              </div>

              {/* Stats grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px 40px',
                fontSize: '12px',
                marginBottom: '36px',
              }}>
                <div>
                  <span style={{ color: PALETTE.DIM, fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Credits</span>
                  <div style={{ color: PALETTE.TEAL, fontSize: '15px', fontWeight: 600 }}>
                    {selectedChar.credits.toLocaleString()}
                  </div>
                </div>
                <div>
                  <span style={{ color: PALETTE.DIM, fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Reputation</span>
                  <div style={{ color: getRepLabel(selectedChar.reputation).color, fontSize: '15px', fontWeight: 600 }}>
                    {getRepLabel(selectedChar.reputation).text}
                  </div>
                </div>
                <div>
                  <span style={{ color: PALETTE.DIM, fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Location</span>
                  <div style={{ color: PALETTE.LIGHT, fontSize: '12px', fontWeight: 500 }}>
                    {selectedChar.constellationName}
                  </div>
                </div>
                <div>
                  <span style={{ color: PALETTE.DIM, fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Coordinates</span>
                  <div style={{ color: PALETTE.LIGHT, fontSize: '12px', fontWeight: 500 }}>
                    ({selectedChar.positionX}, {selectedChar.positionY})
                  </div>
                </div>
              </div>

              {/* ENTER UNIVERSE button */}
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: PALETTE.ORANGE,
                  color: PALETTE.DARK,
                  fontSize: '13px',
                  padding: '14px 60px',
                  minWidth: '260px',
                  borderRadius: '6px',
                  fontWeight: 600,
                }}
                onClick={handlePlay}
              >
                ENTER UNIVERSE
              </button>
            </>
          ) : (
            /* No character selected — empty state */
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: 600, color: PALETTE.LIGHT, marginBottom: '8px' }}>
                No characters yet
              </div>
              <div style={{ fontSize: '11px', fontWeight: 500, color: PALETTE.DIM, marginBottom: '24px', maxWidth: '300px' }}>
                Create your first pilot to enter the universe
              </div>
              {canCreateMore && (
                <button
                  style={{
                    ...buttonStyle,
                    backgroundColor: PALETTE.TEAL,
                    color: PALETTE.DARK,
                    fontSize: '16px',
                    padding: '14px 40px',
                    borderRadius: '6px',
                  }}
                  onClick={() => {
                    setUsername(suggestedUsername);
                    setView('character-creation');
                  }}
                >
                  CREATE CHARACTER
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCharacterCreation = () => (
    <>
      <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: PALETTE.LIGHT }}>
        Choose Your Callsign
      </div>
      <div style={{ fontSize: '12px', color: PALETTE.DIM, marginBottom: '30px', maxWidth: '300px', textAlign: 'center' }}>
        This is how other pilots will know you in the universe
      </div>
      <input
        type="text"
        placeholder="Enter callsign..."
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        style={{
          ...inputStyle,
          fontSize: '16px',
          textAlign: 'center',
          minWidth: '280px',
        }}
        maxLength={20}
        autoFocus
      />
      <div
        style={{
          fontSize: '12px',
          color: usernameAvailable ? PALETTE.TEAL : (usernameStatus === 'Checking...' ? PALETTE.DIM : PALETTE.ORANGE),
          marginBottom: '20px',
          minHeight: '16px',
        }}
      >
        {usernameStatus}
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          minWidth: '280px',
          maxWidth: '280px',
          marginBottom: '20px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <input
          type="checkbox"
          checked={skipTutorial}
          onChange={(e) => setSkipTutorial(e.target.checked)}
          style={{
            marginTop: '2px',
            accentColor: PALETTE.TEAL,
            cursor: 'pointer',
          }}
        />
        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: PALETTE.LIGHT, letterSpacing: '0.5px' }}>
            Skip tutorial
          </span>
          <span style={{ fontSize: '10px', color: PALETTE.DIM, lineHeight: 1.3 }}>
            Spawn in Origin Constellation. You'll skip the Academy walkthrough — this choice is permanent.
          </span>
        </span>
      </label>
      <button
        style={{
          ...buttonStyle,
          backgroundColor: usernameAvailable ? PALETTE.TEAL : PALETTE.DIM,
          color: PALETTE.DARK,
          cursor: usernameAvailable ? 'pointer' : 'not-allowed',
          minWidth: '280px',
        }}
        onClick={handleCreateCharacter}
        disabled={!usernameAvailable || isLoading}
      >
        {isLoading ? 'Creating...' : 'CREATE CHARACTER'}
      </button>
      {characters.length > 0 && (
        <span
          style={linkStyle}
          onClick={() => setView('character-select')}
          onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.LIGHT)}
          onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.DIM)}
        >
          &larr; Back to Characters
        </span>
      )}
    </>
  );

  // Keep the ready view as a simple pass-through (character-select handles ENTER UNIVERSE)
  const renderReadyView = () => renderCharacterSelect();

  return (
    <div style={containerStyle}>
      {renderStarfield()}

      {/* Character select uses full-screen layout — no centered header */}
      {(view === 'character-select' || view === 'ready') ? (
        <>
          {/* Status bar if needed */}
          {status && (
            <div style={{ ...statusStyle, color: statusColor, position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
              {status}
            </div>
          )}
          {renderCharacterSelect()}
          {renderDeleteModal()}
        </>
      ) : (
        <>
          {/* Header - fixed position at top */}
          <div style={{
            position: 'absolute',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <h1 style={{ ...titleStyle, marginBottom: '10px' }}>ASTRA</h1>
            <div style={subtitleStyle}>SPACE WONDERING</div>
            <div style={{ ...versionStyle, marginBottom: 0 }}>{GAME_VERSION} - Pre Alpha</div>
          </div>

          {/* Center content - status and interactive elements */}
          <div style={{ ...statusStyle, color: statusColor, marginBottom: '20px', marginTop: '120px' }}>{status}</div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {view === 'loading' && renderLoadingView()}
            {view === 'initial' && renderInitialView()}
            {view === 'auth-options' && renderAuthOptions()}
            {view === 'email-form' && renderEmailForm()}
            {view === 'server-select' && renderServerSelect()}
            {view === 'character-creation' && renderCharacterCreation()}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes starDrift0 {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes starDrift1 {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes starDrift2 {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes starTwinkle {
          0%, 100% { opacity: inherit; }
          50% { opacity: 0.1; }
        }
      `}</style>
    </div>
  );
}

export default LoginForm;
